import * as React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {
    ApplicationContextConsumer,
    bindLoadingHandler,
    LoadingOverlayHandler,
    withLoadingOverlay
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import {ErrorResponse, Event, EventAlertState, GridRequest, PageResponseEnvelope} from "../api/dtos/dtos";
import * as tableUtils from "../utils/table-utils";
import {filterableColumn, GridState, searchableColumn, simpleColumn, sortableColumn} from "../utils/table-utils";
import locales from "../locales";
import {Button, Table} from "antd";
import ButtonBar from "../common/ButtonBar";
import furoApi from "../api/FuroApi";
import {ColumnProps} from "antd/lib/table";
import {MOMENT_DATE_TIME_FORMAT, toApiDateFormat, toEndOfYear, toStartOfYear} from "../utils/date-utils";
import {createEventDto, EventDto} from "../event-details/dtos";
import * as moment from "moment";
import accountProvider from "../account/AccountProvider";
import {DateRangePicker} from "../common/DateRangePicker";
import ButtonGroup from "antd/lib/button/button-group";
import * as paths from "../paths";
import {showConfirm} from "../utils/dialogs";
import {renderEventTypeElement} from "../client-calendar/EventType";
import {getEventAlertStateFilters, renderEventAlertStateCondensed} from "./eventAlertState";
import {Link} from "react-router-dom";
import {
    AlertController,
    bindAlertController,
    GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";


interface State extends GridState<EventDto> {
    error?: ErrorResponse;
    startFilter?: moment.Moment;
    endFilter?: moment.Moment;
    userName?: string;
    alertState?: EventAlertState[];
}

const STATE_KEY: string = 'alertState';
const EVENT_ALERT = 'alertDate';
const USER_NAME = 'salesUserName';

const initialGridState = (): GridState<EventDto> => {
    return {
        ...tableUtils.initialGridState(),
        filters: {
            'start': [],
            'end': [],
            'salesUserName': accountProvider.isAdmin() ? [] : [accountProvider.getUserName()!],
            'alertState': [EventAlertState.NEW, EventAlertState.TRIGGERED]
        }
    }
};

class EventAlertsPage extends React.Component<RouteComponentProps, State> {

    public readonly loadingHandler?: LoadingOverlayHandler;
    public readonly alertController?: AlertController;

    public state: State = {
        startFilter: toStartOfYear(moment()),
        endFilter: toEndOfYear(moment()),
        sortOrder: "descend",
        sortColumnKey: EVENT_ALERT,

        // Default initial grid state
        ...initialGridState(),

        // Override with cached state from Router
        ...this.props.location.state
    };

    public componentDidMount() {
        this.reload();
    }

    public render(): React.ReactNode {
        const {error, startFilter, endFilter} = this.state;

        const columns: Array<ColumnProps<EventDto>> = [
            {
                ...filterableColumn('', STATE_KEY, this.state, r => renderEventAlertStateCondensed(r.alertState), getEventAlertStateFilters(), true),
                width: 38,
            },
            sortableColumn(locales.event_alert_time, EVENT_ALERT, this.state, (e: EventDto) => e.alertDate?.format(MOMENT_DATE_TIME_FORMAT)),
            simpleColumn(locales.event_time_range_start, (e: EventDto) => e.start.format(MOMENT_DATE_TIME_FORMAT)),
            simpleColumn(locales.event_time_range_end, (e: EventDto) => e.end.format(MOMENT_DATE_TIME_FORMAT)),
            simpleColumn(locales.event_subject, (e: EventDto) => e.subject),
        ];

        if (accountProvider.isAdmin()) {
            columns.push(searchableColumn(locales.event_sales_user, USER_NAME, this.state, (e: EventDto) => e.userName));
        }

        columns.push(...[
            simpleColumn(locales.type, (e: EventDto) => renderEventTypeElement(e.type)),
            simpleColumn(locales.client, this.renderClientCell),
            simpleColumn(locales.contact, (e: EventDto) => e.contact?.name),
            simpleColumn(locales.comment, (e: EventDto) => e.comment ?? '-'),
            {key: 'actions', width: 80, render: this.renderActionColumn,}
        ]);

        return (
            <>
                <ApplicationContextConsumer>
                    {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
                </ApplicationContextConsumer>

                <GlobalAlertContextConsumer>
                    {(state) => bindAlertController(state.event, this)}
                </GlobalAlertContextConsumer>

                <ButtonBar style={{marginBottom: 8}}>
                    <DateRangePicker
                        onRangeChanged={this.onDateRangeFilterChange}
                        defaultStart={startFilter}
                        defaultEnd={endFilter}
                    />
                </ButtonBar>

                <Table
                    dataSource={this.state.data}
                    columns={columns}
                    bordered={true}
                    size="small"
                    rowKey={this.getRowKey}
                    pagination={tableUtils.defaultPaginationConfig(this.state)}
                    onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
                    scroll={{x: 1024}}
                />

                {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}
            </>
        );
    }

    private getRowKey = (record: EventDto): string => {
        return tableUtils.idRowKeyFn(record)
    }

    private onGridChange = (newState: GridState<EventDto>) => {
        this.setState({...newState}, () => this.reload());
    };

    private onDateRangeFilterChange = (start?: moment.Moment, end?: moment.Moment) => {
        this.setState({startFilter: start, endFilter: end}, () => this.reload());
    };

    private reload = () => {
        const request = tableUtils.toGridRequest(this.state);
        this.addExtraFiltersToGridRequest(request);

        const promise = furoApi.eventAlerts.getEventAlerts(request)
            .then(this.onReloadSuccess)
            .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
    };

    private onReloadSuccess = (response: PageResponseEnvelope<Event>) => {
        const dtoResponse: PageResponseEnvelope<EventDto> = {
            results: {
                content: response.results.content.map(e => createEventDto(e)),
                currentPage: response.results.currentPage,
                total: response.results.total
            }
        };

        this.setState({
            ...tableUtils.toGridState(dtoResponse),
            error: undefined,
        });
    };

    private onApiError = (error: ErrorResponse) => {
        this.setState({error})
    };

    private addExtraFiltersToGridRequest(request: GridRequest) {
        const {startFilter, endFilter} = this.state;

        if (startFilter !== null) {
            request.filters = {
                ...request.filters,
                'start': startFilter ? [toApiDateFormat(startFilter)] : [],
            };
        }
        if (endFilter !== null) {
            request.filters = {
                ...request.filters,
                'end': endFilter ? [toApiDateFormat(endFilter)] : [],
            };
        }
    }

    private renderClientCell = (record: EventDto) => {
        return record.client ? <Link to={paths.viewClientPath(record.client!.id)}>{record.client?.name}</Link> : '-';
    };

    private renderActionColumn = (text: any, record: EventDto, index: number) => {
        const onEditEventClick = () => this.onEditRentalItemClick(record, index);
        const onHandleEventClick = () => {
            showConfirm(locales.event_alert_handle, () => this.onHandleConfirmed(record));
        };

        const editable = accountProvider.isAdmin() || accountProvider.getUserName()! === record.userName;
        return (editable &&
            <div className="action-column" style={{textAlign: 'center'}}>
                <ButtonGroup>
                    {!record.alertHandled &&
                        <Button
                            icon={"check-circle"}
                            title={locales.handle}
                            style={{width: 32, padding: 0}}
                            onClick={onHandleEventClick}
                        />
                    }
                    <Button icon="edit" onClick={onEditEventClick} title={locales.edit}/>
                </ButtonGroup>
            </div>
        );
    };

    private onEditRentalItemClick = (event: EventDto, _: number) => {
        this.props.history.push(paths.viewEventPath(event.id));
    };

    private onHandleConfirmed = (event: EventDto) => {
        event.alertHandled = !event.alertHandled;
        const promise = furoApi.events.save(event.id!, event.toEvent())
            .then(this.onEventsChanged)
            .catch(this.onApiError);
        withLoadingOverlay(promise, this.loadingHandler);
    };

    private onEventsChanged = () => {
        this.reload();
        this.alertController?.refreshAlertCount();
    };
}

export default withRouter(EventAlertsPage);



// WEBPACK FOOTER //
// ./src/event-alerts/EventAlertsPage.tsx